import { CreateAuthHeaders, FetchPost } from '../utils/dataModel';

const ApiInfo = [
  {
    method: "POST",
    url: "https://dashboard.faceheart.com/api/auth/key",
    description: "Authenticate with your AppKey to obtain a session ID for accessing other API endpoints. This is the first step required before making any other API calls.",
    request: {
      body: [
        {
          key: "appkey",
          type: "string",
          description: "Your AppKey"
        }
      ],
      example: {
        "uri": "POST https://dashboard.faceheart.com/api/auth/key",
        "body": {
          "appkey": "<appkey>",
        }
      }
    },
    response: {
      body: [
        {
          key: "session_id",
          type: "string",
          description: "Session ID"
        }
      ],
      example:{
        "session_id": "<session_id>"
      }
    }
  },
  {
    method: "GET",
    url: "https://dashboard.faceheart.com/api/v1/dashboard/license",
    description: "Get your license information.",
    request: {
      header: [
        {
          key: "Authorization",
          value: "Bearer <session_id>",
          description: "Session ID from AppKey authorization."
        }
      ],
      example: {
        "uri": "GET https://dashboard.faceheart.com/api/v1/dashboard/license",
        "header": "Authorization: Bearer <session_id>"
      }
    },
    response: {
      body: [
        {
          key: "license_id",
          type: "string",
          description: "License ID"
        },
        {
          key: "license_key",
          type: "string",
          description: "License key"
        },
        {
          key: "number_of_licenses",
          type: "integer",
          description: "Total number of licenses"
        },
        {
          key: "number_of_licenses_used",
          type: "integer",
          description: "Number of licenses used"
        },
        {
          key: "expiration_date",
          type: "string",
          description: "License expiration date"
        },
        {
          key: "billing_type",
          type: "string",
          description: "Billing type, e.g., 'Pay per use', 'Per Member Per Month'"
        },
        {
          key: "sdk_type",
          type: "string",
          description: "SDK type, e.g., 'Standard', 'Advance'"
        },
        {
          key: "license_mode",
          type: "string",
          description: "License mode, e.g., 'Trial', 'Official'"
        }
      ],
      example: [{
        "license_id": "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        "license_key": "XXXX:XXXX:XXXX:XXXX:XXXX",
        "number_of_licenses": 10,
        "number_of_licenses_used": 5,
        "expiration_date": "2025-12-31",
        "billing_type": "Pay per use",
        "sdk_type": "Standard",
        "license_mode": "Trial"
      }],
      error: [
        {
          status: 401,
          message: "Session expired",
          description: "The session ID has expired. Please re-authenticate using your AppKey."
        },
        {
          status: 401,
          message: "Session auth IP mismatch",
          description: "The session ID is being used from a different IP address than the one it was issued from. Please re-authenticate using your AppKey."
        },
      ]
    }
  },
  {
    method: "GET",
    url: "https://dashboard.faceheart.com/api/v1/dashboard/realtime",
    description: "Get real-time information for a specific license.",
    request: {
      query: [
        {
          key: "license_id",
          type: "string",
          description: "License ID"
        }
      ],
      header: [
        {
          key: "Authorization",
          value: "Bearer <session_id>",
          description: "Session ID from AppKey authorization."
        }
      ],
      example: {
        "uri": "GET https://dashboard.faceheart.com/api/v1/dashboard/realtime?license_id=<license_id>",
        "header": "Authorization: Bearer <session_id>"
      }
    },
    response: {
      body: [
        {
          key: "timestamp",
          type: "string",
          description: "Timestamp of the data"
        },
        {
          key: "num_licenses_new_used",
          type: "integer",
          description: "Number of new licenses used"
        },
        {
          key: "usage_total",
          type: "integer",
          description: "Total usage"
        },
        {
          key: "country",
          type: "string",
          description: "Country"
        }
      ],
      example: [
        {
          "timestamp": "2025-01-01 00:00:00",
          "num_licenses_new_used": 1,
          "usage_total": 0,
          "country": "United States"
        },
        {
          "timestamp": "2025-01-01 03:00:00",
          "num_licenses_new_used": 2,
          "usage_total": 0,
          "country": "United States"
        },
        {
          "timestamp": "2025-01-01 21:00:00",
          "num_licenses_new_used": 0,
          "usage_total": 1,
          "country": "Canada"
        },
        {
          "timestamp": "2025-01-02 03:00:00",
          "num_licenses_new_used": 1,
          "usage_total": 0,
          "country": "United States"
        },
        {
          "timestamp": "2025-01-02 13:00:00",
          "num_licenses_new_used": 1,
          "usage_total": 1,
          "country": "United States"
        },
        {
          "timestamp": "2025-01-02 14:00:00",
          "num_licenses_new_used": 1,
          "usage_total": 5,
          "country": "United States"
        },
        {
          "timestamp": "2025-01-03 20:00:00",
          "num_licenses_new_used": 0,
          "usage_total": 1,
          "country": "Canada"
        },
        {
          "license_key": "XXXX:XXXX:XXXX:XXXX:XXXX",
          "timestamp": "2025-01-03 21:00:00",
          "num_licenses_new_used": 0,
          "usage_total": 2,
          "country": "Canada"
        }
      ],
      error: [
        {
          status: 401,
          message: "Session expired",
          description: "The session ID has expired. Please re-authenticate using your AppKey."
        },
        {
          status: 401,
          message: "Session auth IP mismatch",
          description: "The session ID is being used from a different IP address than the one it was issued from. Please re-authenticate using your AppKey."
        },
      ]
    }
  },
  {
    method: "GET",
    url: "https://dashboard.faceheart.com/api/v1/dashboard/history",
    description: "Get historical information for a specific license within a date range.",
    request: {
      query: [
        {
          key: "license_id",
          type: "string",
          description: "License ID"
        },
        {
          key: "from",
          type: "string",
          description: "Start date in YYYY-MM-DD format"
        },
        {
          key: "to",
          type: "string",
          description: "End date in YYYY-MM-DD format"
        }
      ],
      header: [
        {
          key: "Authorization",
          value: "Bearer <session_id>",
          description: "Session ID from AppKey authorization."
        }
      ],
      example: {
        "uri": "GET https://dashboard.faceheart.com/api/v1/dashboard/history?license_id=<license_id>&from=2025-01-01&to=2025-01-02",
        "header": "Authorization: Bearer <session_id>"
      }
    },
    response: {
      body: [
        {
          key: "timestamp",
          type: "string",
          description: "Timestamp of the data"
        },
        {
          key: "num_licenses_new_used",
          type: "integer",
          description: "Number of new licenses used"
        },
        {
          key: "usage_total",
          type: "integer",
          description: "Total usage"
        },
        {
          key: "num_licenses_active",
          type: "integer",
          description: "Number of active licenses"
        },
        {
          key: "num_licenses_used",
          type: "integer",
          description: "Number of licenses used"
        },
        {
          key: "usage_distribution",
          type: "array",
          description: "Distribution of usage"
        },
        {
          key: "platform_distribution",
          type: "array",
          description: "Distribution of platforms"
        },
        {
          key: "app_id_distribution",
          type: "array",
          description: "Distribution of app IDs"
        }
      ],
      example: [
        {
          "timestamp": "2025-01-01",
          "num_licenses_new_used": 2,
          "usage_total": 1,
          "num_licenses_active": 3,
          "num_licenses_used": 5,
          "usage_distribution": [
            {
              "num": 1,
              "usage": "1"
            }
          ],
          "platform_distribution": [
            {
              "num": 2,
              "platform": "iOS"
            }
          ],
          "app_id_distribution": [
            {
              "num": 2,
              "app_id": "com.ios.xxx"
            }
          ]
        },
        {
          "timestamp": "2025-01-02",
          "num_licenses_new_used": 1,
          "usage_total": 0,
          "num_licenses_active": 4,
          "num_licenses_used": 5,
          "usage_distribution": [
            {
              "num": 1,
              "usage": "1"
            },
            {
              "num": 2,
              "usage": "2"
            }
          ],
          "platform_distribution": [
            {
              "num": 1,
              "platform": "Android"
            }
          ],
          "app_id_distribution": [
            {
              "num": 1,
              "app_id": "com.android.xxx"
            }
          ]
        }
      ],
      error: [
        {
          status: 401,
          message: "Session expired",
          description: "The session ID has expired. Please re-authenticate using your AppKey."
        },
        {
          status: 401,
          message: "Session auth IP mismatch",
          description: "The session ID is being used from a different IP address than the one it was issued from. Please re-authenticate using your AppKey."
        },
      ]
    }
  }
];

class ApiInfoService {
  apiInfo = ApiInfo;

  applyAppKey() {
    return new Promise((resolve, reject) => {
      const api = `/api/auth/appkey/apply`;
      FetchPost(api, CreateAuthHeaders("session_id"))
        .then(data => {
          resolve(data.success);
        })
        .catch(error => {
          console.error('Failed to request API key:', error);
          reject(error);
        });
    });
  }
}

const api_info_service = new ApiInfoService();
export default api_info_service;
